/* Font registry. Each family is loaded twice from the same .ttf: once as a
   FontFace so the browser can paint the preview, and once through opentype.js
   so we can measure advances and convert glyphs to outlines at export time.

   Using one file for both is what keeps the preview and the PDF identical —
   the same advance widths drive wrapping on screen and glyph placement in the
   PDF, and kerning is switched off on both sides so they cannot diverge. */

window.JCARD = window.JCARD || {};

JCARD.fonts = (function () {
  /* Locally the fonts sit in ./fonts/ and are fetched directly.

     On Shopify they cannot be: the CDN transcodes .ttf theme assets to WOFF2
     on delivery, and opentype.js needs the original TrueType to read glyph
     outlines. So the build wraps each font as base64 inside a .js asset, which
     the CDN passes through untouched, and the Liquid section publishes a
     filename -> script URL map. Scripts are pulled in only when a font is
     actually used, so a page load costs the two or three families on the card
     rather than all ten. */
  const DIR = 'fonts/';

  function urlFor(filename) {
    const map = window.JCARD_FONT_URLS;
    if (map && map[filename]) return map[filename];
    return DIR + filename;
  }

  /* Must match scriptNameFor() in tools/build-shopify.mjs. */
  function scriptNameFor(filename) {
    return `jcard-font-${filename.replace(/\.ttf$/, '').toLowerCase()}.js`;
  }

  function base64ToBuffer(b64) {
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return bytes.buffer;
  }

  function loadScript(src) {
    return new Promise((resolve, reject) => {
      const tag = document.createElement('script');
      tag.src = src;
      tag.onload = resolve;
      tag.onerror = () => reject(new Error(`could not load ${src}`));
      document.head.appendChild(tag);
    });
  }

  /* Raw TrueType bytes for one file, from whichever delivery route exists. */
  function bytesFor(filename) {
    const data = window.JCARD_FONT_DATA;
    if (data && data[filename]) return Promise.resolve(base64ToBuffer(data[filename]));

    /* Either an explicit per-file map, or — the usual case — one asset base
       published by the Liquid block, off which the script names are derived.
       The base keeps the Liquid stable: adding or removing a family changes
       only the uploaded assets, never the pasted block. */
    const scripts = window.JCARD_FONT_SCRIPTS;
    const base = window.JCARD_ASSET_BASE;
    const src = (scripts && scripts[filename])
      || (base ? base + scriptNameFor(filename) : null);

    if (src) {
      return loadScript(src).then(() => {
        const b64 = (window.JCARD_FONT_DATA || {})[filename];
        if (!b64) throw new Error(`${filename} script loaded but held no data`);
        const buf = base64ToBuffer(b64);
        delete window.JCARD_FONT_DATA[filename];   // parsed copy is enough
        return buf;
      });
    }

    return fetch(urlFor(filename)).then((r) => {
      if (!r.ok) throw new Error(`${filename}: ${r.status}`);
      return r.arrayBuffer();
    });
  }

  const FAMILIES = [
    { name: 'Archivo', weights: { 400: 'Archivo-Regular.ttf', 700: 'Archivo-Bold.ttf' } },
    { name: 'Archivo Narrow', weights: { 400: 'ArchivoNarrow-Regular.ttf', 700: 'ArchivoNarrow-Bold.ttf' } },
    { name: 'Libre Franklin', weights: { 400: 'LibreFranklin-Regular.ttf', 700: 'LibreFranklin-Bold.ttf' } },
    { name: 'Oswald', weights: { 400: 'Oswald-Regular.ttf', 700: 'Oswald-Bold.ttf' } },
    { name: 'Bebas Neue', weights: { 400: 'BebasNeue-Regular.ttf', 700: 'BebasNeue-Regular.ttf' } },
    { name: 'Space Mono', weights: { 400: 'SpaceMono-Regular.ttf', 700: 'SpaceMono-Bold.ttf' } },
    { name: 'Courier Prime', weights: { 400: 'CourierPrime-Regular.ttf', 700: 'CourierPrime-Bold.ttf' } },
    { name: 'IBM Plex Mono', weights: { 400: 'IBMPlexMono-Regular.ttf', 700: 'IBMPlexMono-Bold.ttf' } },
    { name: 'DM Sans', weights: { 400: 'DMSans-Regular.ttf', 700: 'DMSans-Bold.ttf' } },
    { name: 'Inter', weights: { 400: 'Inter-Regular.ttf', 700: 'Inter-Bold.ttf' } },
  ];

  // Bebas Neue ships in a single weight; asking for bold gets the same file.
  const SINGLE_WEIGHT = new Set(['Bebas Neue']);

  const parsed = new Map();   // "Family|weight" -> opentype.Font
  const pending = new Map();

  function key(family, weight) {
    return family + '|' + (SINGLE_WEIGHT.has(family) ? 400 : weight);
  }

  function nameFor(family, weight) {
    const fam = FAMILIES.find((f) => f.name === family) || FAMILIES[0];
    return fam.weights[SINGLE_WEIGHT.has(family) ? 400 : weight] || fam.weights[400];
  }

  function fileFor(family, weight) {
    return urlFor(nameFor(family, weight));
  }

  /* One buffer serves both consumers: the browser paints from it via FontFace,
     opentype.js measures and outlines from it. They cannot disagree. */
  function load(family, weight) {
    const k = key(family, weight);
    if (parsed.has(k)) return Promise.resolve(parsed.get(k));
    if (pending.has(k)) return pending.get(k);

    const job = bytesFor(nameFor(family, weight)).then((buf) => {
      const font = window.opentype.parse(buf);
      parsed.set(k, font);
      return new FontFace(family, buf, { weight: String(weight), style: 'normal' })
        .load()
        .then((ff) => { document.fonts.add(ff); return font; })
        .catch(() => font);   // measurement still works if the paint face fails
    });

    pending.set(k, job);
    return job;
  }

  function loadAll() {
    const jobs = [];
    FAMILIES.forEach((f) => {
      jobs.push(load(f.name, 400));
      if (!SINGLE_WEIGHT.has(f.name)) jobs.push(load(f.name, 700));
    });
    return Promise.all(jobs);
  }

  /* The families a given design actually needs. Archivo is always in the list:
     the A/B badges and the artwork placeholder are set in it regardless of what
     the user picks for the editable fields. */
  function neededBy(state) {
    const want = new Map();
    const add = (family, weight) => {
      if (!FAMILIES.some((f) => f.name === family)) return;
      want.set(key(family, weight), [family, weight]);
    };
    add('Archivo', 400);
    add('Archivo', 700);
    Object.keys(state.fields).forEach((id) => {
      const f = state.fields[id];
      if (f && f.font) add(f.font, f.weight || 400);
    });
    return Array.from(want.values());
  }

  /* Resolves true when something new arrived, so the caller knows to repaint. */
  function ensureFor(state) {
    const missing = neededBy(state).filter(([fam, w]) => !parsed.has(key(fam, w)));
    if (!missing.length) return Promise.resolve(false);
    return Promise.all(missing.map(([fam, w]) => load(fam, w))).then(() => true);
  }

  function get(family, weight) {
    return parsed.get(key(family, weight)) || null;
  }

  /* Advance width of a string in millimetres, at the given size and tracking.
     Kerning is off so this matches what the browser paints and what the PDF
     outliner produces. Tracking is expressed as a fraction of the font size,
     matching the CSS letter-spacing we set in em. */
  function measure(family, weight, text, size, tracking) {
    const font = get(family, weight);
    if (!font || !text) return 0;
    const scale = size / font.unitsPerEm;
    let width = 0;
    const glyphs = font.stringToGlyphs(text);
    glyphs.forEach((g) => { width += (g.advanceWidth || 0) * scale; });
    width += (tracking || 0) * size * glyphs.length;
    return width;
  }

  /* Glyph outlines for one run of text, as SVG path data. x/y is the baseline
     start; tracking and anchor match measure() so a laid-out line lands in the
     same place whether it is painted as <text> or exported as <path>. */
  function outline(family, weight, text, x, y, size, tracking, anchor) {
    const font = get(family, weight);
    if (!font || !text) return '';
    const scale = size / font.unitsPerEm;
    const width = measure(family, weight, text, size, tracking);
    let penX = x;
    if (anchor === 'middle') penX -= width / 2;
    else if (anchor === 'end') penX -= width;

    const path = new window.opentype.Path();
    font.stringToGlyphs(text).forEach((g) => {
      const gp = g.getPath(penX, y, size);
      path.extend(gp);
      penX += (g.advanceWidth || 0) * scale + (tracking || 0) * size;
    });
    return path.toPathData(3);
  }

  return {
    FAMILIES,
    names: FAMILIES.map((f) => f.name),
    SINGLE_WEIGHT,
    load,
    loadAll,
    ensureFor,
    neededBy,
    get,
    measure,
    outline,
    fileFor,
  };
})();
